import { useCallback, useContext, useEffect, useState } from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { useSubmitHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import { usePostPassportFormMutation } from '@Features/EmployeeSelfService/requests/PassportRequest/store/api/passportRequestApiSlice';
import {
  getPassportTypeOptions,
  passportRequestCreateRequisitionInfo,
} from '@Features/EmployeeSelfService/requests/PassportRequest/store/selectors/passportRequestSelector';
import { passportRequestActions } from '@Features/EmployeeSelfService/requests/PassportRequest/store/passportRequestSlice';
import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
} from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { RequestError, PassportRequestFormPostRequest } from '@Types/api/form';
import { PassportReqTypes } from '@Enum/api/PassportReqTypes.enum';
import { getTotalDays } from '@Utils/transformDateTime';
import { validateForm } from '@Features/utils/validateForm';
import { NotificationContext } from '@Context/NotificationContext';
import { FooterActions } from '@Enum/index';

import { PassportRequestFormValues } from '../type';
import RequestDetailsSection from '../components/RequestDetailsSection';
import { PassportRequestFormSchema } from '../validationSchema';
import { initialValuesForm } from '../initialValues';

const descriptionAlert = (
  <p>
    Please enter all the details and submit the data. After submission Passport
    request will go to higher officials approval and you can cancel it at any
    point of time before it gets approved by HR departments.
    <br />
    Each request will be monitored by HR & PRO department.
  </p>
);

const PassportRequestCreateForm: React.FC = () => {
  const {
    saveSuccessNotification,
    errorNotification,
    submitSuccessNotification,
  } = useContext(NotificationContext);
  const passportTypeOptions = useAppSelector(getPassportTypeOptions);
  const currentUser = useAppSelector(currentUserSelector);
  const requisitionInfo = useAppSelector(passportRequestCreateRequisitionInfo);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const dispatch = useAppDispatch();
  const [postPassportForm] = usePostPassportFormMutation();
  const [submitForm] = useSubmitHrmRequestMutation();
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(initialValuesForm);

  useEffect(
    () => () => {
      dispatch(passportRequestActions.setPassportRequest(null));
    },
    []
  );

  useEffect(() => {
    if (!passportTypeOptions.length) {
      return;
    }

    setInitialFormValues({
      ...initialValuesForm,
      purpose: `${PassportReqTypes.PP_RENEWAL}`,
    });
  }, [passportTypeOptions]);

  const handleSubmitForm = useCallback(
    (values: PassportRequestFormValues) => {
      const submitHandler = async (): Promise<void> => {
        let body = {
          empNumber: currentUser.empNumber,
          leaveTypeID: Number(values.purpose),
          startDate: values.fromDate,
          endDate: values.returnDate,
          totDays: Number(values.totDays),
          remarks: values.remarks,
        } as PassportRequestFormPostRequest;

        if (requisitionInfo?.requisitionNo) {
          body = {
            ...body,
            requestId: Number(requisitionInfo?.requisitionNo),
          };
        }

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            reqStatusId,
            requestId: reqId,
          } = await postPassportForm(body).unwrap();

          dispatch(
            passportRequestActions.setPassportRequest({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          await submitForm(String(reqId)).unwrap();

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitHandler();
    },
    [currentUser, requisitionInfo, handleBackToListRequests]
  );

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: PassportRequestFormSchema,
    onSubmit: handleSubmitForm,
  });

  const handleSaveAsDraft = useCallback((): void => {
    const saveAsDraft = async (): Promise<void> => {
      const body = {
        empNumber: currentUser.empNumber,
        leaveTypeID: Number(formik.values.purpose),
        startDate: formik.values.fromDate,
        endDate: formik.values.returnDate,
        totDays: Number(formik.values.totDays),
        remarks: formik.values.remarks,
      } as PassportRequestFormPostRequest;

      setDisableSaveForm(true);

      try {
        const {
          requestId: reqId,
          createdDate,
          reqStatusId,
        } = await postPassportForm(body).unwrap();

        dispatch(
          passportRequestActions.setPassportRequest({
            createdDate,
            requestId: reqId,
            reqStatusId,
          })
        );

        saveSuccessNotification(String(reqId));

        setDisableSaveForm(false);

        handleBackToListRequests();
      } catch (error) {
        const message = (error as RequestError)?.data;
        errorNotification(message);

        setDisableSaveForm(false);
      }
    };

    const saveAsDraftHandler = async (): Promise<void> => {
      const isFormValid = await validateForm(PassportRequestFormSchema, formik);

      if (!isFormValid) {
        return;
      }

      await saveAsDraft();
    };

    saveAsDraftHandler();
  }, [currentUser, formik, handleBackToListRequests]);

  useEffect(() => {
    if (formik.values.fromDate && formik.values.returnDate) {
      const totalDays = getTotalDays(
        formik.values.returnDate,
        formik.values.fromDate
      );

      formik.setFieldValue('totDays', totalDays);
    } else {
      formik.setFieldValue('totDays', '');
    }
  }, [formik.values.fromDate, formik.values.returnDate]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSaveAsDraft();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSaveAsDraft]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        descriptionAlert={descriptionAlert}
        disabledSaveForm={disabledSaveForm}
        formType="create"
      >
        <FormItems>
          <RequestDetailsSection
            label="Request Details"
            formik={formik}
            formApproval={false}
            passportTypeOptions={passportTypeOptions}
          />
        </FormItems>
      </MainForm>
    </Flex>
  );
};

export default PassportRequestCreateForm;
